import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { lightThemeColors } from "../../assets/styles/colors";
import PrimaryButton from "../../Components/Generic/PrimaryButton";
import { ReactComponent as PlusIcon } from "../../assets/icons/plusIcon.svg";
import { ReactComponent as CopyIcon } from "../../assets/icons/copyIcon.svg";
import { ReactComponent as DotMenuIcon } from "../../assets/icons/dotMenuIcon.svg";
import { ReactComponent as BinIcon } from "../../assets/icons/bin-emotii.svg";
import SecondaryButton from "../../Components/Generic/SecondaryButton";
import { AnimatePresence } from "framer-motion";
import InviteMembersModal from "../../Components/MembersPage/InviteMembersModal";
import { useMembersContext } from "../../context/useMembersContext";
import { useProjectContext } from "../../context/useProjectContext";
import { useOutletContext } from "react-router-dom";
import { showGenericError } from "../../helper/Notifications";
import EditMemberModal from "../../Components/MembersPage/EditMemberModal";
import { useUserContext } from "../../context/useUserContext";
import AdminRoleRequired from "../../helper/AdminRoleRequired";
import StyledTable, {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../Components/Generic/StyledTable";
import TablePagination from "../../Components/Generic/TablePagination";
import CustomModal from "../../Components/Generic/CustomModal";
import CustomInput from "../../Components/Generic/CustomInput";
import Pill from "../../Components/Generic/Pill";
import Dropdown from "../../Components/Generic/Dropdown";
import { useLicensingContext } from "../../context/useLicensingContext";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";

const MembersPage = ({ embedded = false, showInviteMembers: externalShowInviteMembers, setShowInviteMembers: externalSetShowInviteMembers }) => {
  const { selectedProject, isAdmin } = useProjectContext();
  const { platformProducts, subscription } = useLicensingContext();
  const { user } = useUserContext();
  const { setLoading } = useOutletContext();
  const [internalShowInviteMembers, setInternalShowInviteMembers] = useState(false);

  // Use external state if embedded, otherwise use internal state
  const showInviteMembers = embedded ? externalShowInviteMembers : internalShowInviteMembers;
  const setShowInviteMembers = embedded ? externalSetShowInviteMembers : setInternalShowInviteMembers;
  const { getMembersList, changeMemberRole, deleteMember } =
    useMembersContext();
  const [editMemberModal, setEditMemberModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebouncedValue(searchTerm.trim(), 300);
  const [roleFilter, setRoleFilter] = useState("all");
  const [members, setMembers] = useState(undefined);

  const handleGetMembers = (projectId) => {
    getMembersList(
      projectId,
      page,
      itemsPerPage,
      debouncedSearchTerm,
      (response) => {
        setMembers(response.data.members);
        setTotalPages(response.data.pagination?.total_pages || 1);
        setLoading(false);
      },
      () => {
        showGenericError();
        setLoading(false);
      },
      roleFilter
    );
  };

  useEffect(() => {
    if (selectedProject) {
      handleGetMembers(selectedProject.id);
    }
  }, [selectedProject, page, itemsPerPage, debouncedSearchTerm, roleFilter]);

  const handleEditMember = (member) => {
    if (member.user.id !== user.id && isAdmin) {
      setSelectedMember(member);
      setEditMemberModal(true);
    }
  };

  const praseRole = (member) => {
    if (member.role === "admin") return "Administrator";
    if (member.role === "member") {
      const slugs = member.product_slugs ?? [];
      if (slugs.length === 0) return "Member";
      return slugs
        .map(slug => {
            const found = platformProducts.find(p => p.slug === slug);
            return found ? found.name : slug;
        })
        .join(", ");
    }
    return "Role";
  };

  const handleChangeRole = (role, productSlugs = []) => {
    setLoading(true);
    changeMemberRole(
      selectedProject.id,
      selectedMember.id,
      role,
      productSlugs,
      (response) => {
        setEditMemberModal(false);
        setSelectedMember(null);
        handleGetMembers(selectedProject.id);
      },
      (error) => {
        setLoading(false);
        showGenericError();
      }
    );
  };

  const handleRemoveMember = (member) => {
    setLoading(true);
    deleteMember(
      selectedProject.id,
      member.id,
      (response) => {
        setEditMemberModal(false);
        setSelectedMember(null);
        handleGetMembers(selectedProject.id);
      },
      (error) => {
        showGenericError();
        setLoading(false);
      }
    );
  };


  const handleOpenDeleteModal = (member) => {
    setMemberToDelete(member);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setMemberToDelete(null);
  };

  const handleDeleteMemberFromTable = () => {
    return new Promise((resolve, reject) => {
      handleRemoveMember(memberToDelete);
      resolve();
    });
  };

  return (
    <Container>
      <CustomModal
        isOpen={showDeleteModal}
        onOpenChange={handleCloseDeleteModal}
        iconType="warning"
        iconAlignment="center"
        title="Remove this member?"
        titleAlignment="center"
        buttonLayout="row"
        showCloseButton={false}
        autoCloseOnSuccess={true}
        width={400}
        buttons={[
          {
            text: "Cancel",
            onClick: handleCloseDeleteModal,
            variant: "secondary"
          },
          {
            text: "Delete",
            action: handleDeleteMemberFromTable,
            variant: "delete",
            successMessage: "Member removed successfully"
          }
        ]}
      >
        <ModalContentText>
          This member will immediately lose access to this project.
        </ModalContentText>
        <ModalContentText>
          This action cannot be undone.
        </ModalContentText>
      </CustomModal>

      <AnimatePresence>
        {showInviteMembers && (
          <InviteMembersModal
            setIsModalOpen={setShowInviteMembers}
            onInvited={() => handleGetMembers(selectedProject.id)}
          />
        )}
        {editMemberModal && (
          <EditMemberModal
            setIsModalOpen={setEditMemberModal}
            member={selectedMember}
            handleChangeRole={handleChangeRole}
          />
        )}
      </AnimatePresence>
      <PageContent>
        {!embedded && (
          <Header>
            <HeaderTitle>Access</HeaderTitle>
            {isAdmin && (
              <PrimaryButton
                styled={{ marginLeft: "auto" }}
                icon={<PlusIcon />}
                text={"Add Admin/Member"}
                onClick={() => setShowInviteMembers(true)}
              />
            )}
          </Header>
        )}
        <TableSection>
          <SearchContainer>
            <CustomInput
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.currentTarget.value);
                setPage(1);
              }}
              placeholder="Search members..."
              wrapperStyle={{ maxWidth: "400px" }}
              className="h-9 py-1.5"
            />
            <SearchSpacer />
            <div style={{ minWidth: 160 }}>
              <Dropdown
                value={roleFilter}
                onValueChange={(v) => { setRoleFilter(v); setPage(1); }}
                placeholder="All users"
                className="h-9 py-1.5"
                options={[
                  { value: "all", label: "All users" },
                  { value: "admin", label: "Admin" },
                  { value: "member", label: "Member" },
                ]}
              />
            </div>
            {embedded && isAdmin && (
              <PrimaryButton
                icon={<PlusIcon style={{ width: "14px", height: "14px" }} />}
                text={"Add Member"}
                onClick={() => setShowInviteMembers(true)}
              />
            )}
          </SearchContainer>
          <StyledTable>
          <TableHeader>
            <TableRow>
              <TableHead style={{ textAlign: "left", width: "50%" }}>Email</TableHead>
              <TableHead style={{ textAlign: "center", width: "160px" }}>Role</TableHead>
              {isAdmin && (
                <TableHead style={{ width: "100px", textAlign: "center" }}>Actions</TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {members?.length === 0 && (
              <TableRow>
                <TableCell colSpan={isAdmin ? 3 : 2}>
                  <EmptyStateText>
                    {searchTerm
                      ? `No members matching "${searchTerm}"`
                      : "No members"}
                  </EmptyStateText>
                </TableCell>
              </TableRow>
            )}
            {members?.map((member, index) => {
              const isCurrentUser = member.user.id === user.id;

              return (
                <TableRow
                  key={member.id}
                >
                  <TableCell>
                    <UserEmailContainer>
                      <UserAvatar>
                        <UserIcon />
                      </UserAvatar>
                      <UserEmail data-emotii-exclude>{member.user.email}</UserEmail>
                    </UserEmailContainer>
                  </TableCell>
                  <TableCell style={{ textAlign: "center" }}>
                    <Pill variant={member.role === "admin" ? "success" : "default"} size="sm" data-emotii-exclude>
                        {praseRole(member)}
                    </Pill>
                  </TableCell>
                  {isAdmin && (
                    <TableCell style={{ width: "100px", textAlign: "center" }}>
                      <ActionsContainer>
                        <ActionButton
                          onClick={() => handleEditMember(member)}
                          disabled={isCurrentUser}
                          title={isCurrentUser ? "You can't change your own role" : "Change role"}
                        >
                          <DotMenuIcon />
                        </ActionButton>
                        <ActionButton
                          onClick={() => handleOpenDeleteModal(member)}
                          disabled={isCurrentUser}
                          title={isCurrentUser ? "You can't remove yourself" : "Remove member"}
                          className="delete"
                        >
                          <BinIcon />
                        </ActionButton>
                      </ActionsContainer>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
          <TablePagination
            page={page}
            totalPages={totalPages}
            onPrevious={() => setPage((p) => Math.max(1, p - 1))}
            onNext={() => setPage((p) => p + 1)}
            leftColSpan={1}
            centerColSpan={1}
            rightColSpan={1}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={(v) => { setItemsPerPage(v); setPage(1); }}
          />
          </StyledTable>
        </TableSection>
      </PageContent>
    </Container>
  );
};

export default MembersPage;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

const PageContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  overflow: hidden;
  padding-bottom: 30px;
  position: relative;
  height: 100%;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
`;

const HeaderTitle = styled.h1`
  color: ${lightThemeColors.darkColor};
  font-size: 24px;
  font-weight: 600;
  line-height: 36px;
  text-align: left;
  margin: 0;
`;

const UserEmailContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #dbeafe;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const UserIcon = styled.div`
  width: 20px;
  height: 20px;
  background: #3b82f6;
  border-radius: 50%;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 3px;
    left: 50%;
    transform: translateX(-50%);
    width: 8px;
    height: 8px;
    background: white;
    border-radius: 50%;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 2px;
    left: 50%;
    transform: translateX(-50%);
    width: 12px;
    height: 6px;
    background: white;
    border-radius: 6px 6px 0 0;
  }
`;

const UserEmail = styled.span`
  color: ${lightThemeColors.darkColor};
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
`;

const ActionsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  transition: all 0.2s;

  &:hover {
    background: #f3f4f6;
    color: #374151;
  }

  &.delete:hover {
    background: #fef2f2;
    color: #ef4444;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }

  &:disabled:hover,
  &.delete:disabled:hover {
    background: none;
    color: #9ca3af;
  }

  svg {
    width: 16px;
    height: 16px;
    fill: none !important;
    stroke: currentColor !important;
    stroke-width: 1.5 !important;
  }

  svg path {
    fill: none !important;
    stroke: currentColor !important;
    stroke-width: 1.5 !important;
  }
`;

const TableSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 4px;
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const SearchSpacer = styled.div`
  flex: 1;
`;

const EmptyStateText = styled.p`
  color: #667085;
  font-size: 14px;
  text-align: center;
  margin: 0;
  padding: 16px 0;
`;

const ModalContentText = styled.p`
  color: #667085;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  text-align: center;
  margin: 0;
`;

